import { useState, useEffect, useCallback } from 'react';
import { authService } from '@services/authService';
import type { User } from '@types/index';

interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
}

/**
 * Custom hook for authentication management
 * Manages user authentication state and provides auth methods
 */
export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize authentication
  useEffect(() => {
    const initAuth = async () => {
      try {
        const authenticated = await authService.init();
        setIsAuthenticated(authenticated);

        if (authenticated) {
          const currentUser = authService.getUser();
          setUser(currentUser);

          // Store token in localStorage for microfrontends to access
          const token = authService.getToken();
          if (token) {
            localStorage.setItem('kc_token', token);
          }
        } else {
          // Clear token from localStorage when not authenticated
          localStorage.removeItem('kc_token');
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void initAuth();
  }, []);

  const login = useCallback(async () => {
    await authService.login();
  }, []);

  const logout = useCallback(async () => {
    localStorage.removeItem('kc_token');
    await authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const hasRole = useCallback(
    (role: string): boolean => {
      return authService.hasRole(role);
    },
    []
  );

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    hasRole,
  };
}
